import moment from 'moment';
import { TimeOfDay } from '@tools/ui/enums';

class DatesUtil {
  format: string;

  datePickerFormat: string;

  constructor() {
    this.format = 'MM/DD/YYYY';
    this.datePickerFormat = 'MMM D, YYYY';
  }

  HOURS_FORMAT = 'hh:mm A';
  FULL_DATE_WITH_HOURS_FORMAT = 'DD MMM, YYYY hh:mm A';

  getDateHour = (date?: string): string => {
    return moment(date).format('HH:mm');
  };

  titleFormattedDate = (date?: string): string => {
    return moment(date).format('dddd D, MMMM YYYY');
  };

  formattedDate = (date?: string): string => {
    return moment(date).format(this.datePickerFormat);
  };

  timeSince = (date?: string): string => {
    return moment(date).fromNow();
  };

  timeFromDate = (date?: string): string => {
    return moment(date).format('HH:mm');
  };

  formattedMoment = (date?: string): moment.Moment => {
    return moment(moment(date).format(this.format));
  };

  moment = (date?: string): moment.Moment => moment(date);

  formatDate = (date?: string | Date, format?: string): string => {
    return moment(date)
      .format(format || this.format)
      .toString();
  };

  addDays = (
    date: string,
    days: number,
    format = this.datePickerFormat
  ): string => {
    return moment(date).add(days, 'days').format(format);
  };

  subtractDays = (
    date: Date,
    days: number,
    format = this.datePickerFormat
  ): string => {
    return moment(date).subtract(days, 'days').format(format);
  };

  getTimeOfDay = (date: Date) => {
    const hours: number | undefined = date?.getHours();
    return hours < 12
      ? TimeOfDay.morning
      : hours < 18
      ? TimeOfDay.afternoon
      : TimeOfDay.evening;
  };

  getIsoString = (date?: string): string => this.moment(date).toISOString();

  getNumOfDays = (fromDate: moment.Moment, toDate: moment.Moment): number =>
    moment.duration(toDate.diff(fromDate)).asDays();

  // TODO: Add default value to return when date is undefined instead of 'N/A'
  getFormattedDate = (date?: string, format = 'DD MMM, YYYY'): string => {
    return date ? moment(date).format(format) : 'N/A';
  };

  getDifferenceDays = (date1: Date, date2: Date): number => {
    const differenceInTime = date1.getTime() - date2.getTime();
    const differenceInDays = differenceInTime / (1000 * 3600 * 24);

    return Math.round(differenceInDays);
  };

  getDifferenceInDays = (date1: Date, date2: Date): number => {
    const date1InMoment = moment(date1);
    const date2InMoment = moment(date2);
    const difference = date1InMoment.diff(date2InMoment, 'days');
    return Math.abs(difference);
  };

  /**
   * Parse a numeric-string timestamp (milliseconds since epoch) into a Date.
   */
  parseTimestamp = (timestamp: string | number): Date =>
    new Date(Number(timestamp));

  /**
   * Convert a numeric-string timestamp to an ISO-8601 string.
   */
  toISOFromTimestamp = (timestamp: string | number): string =>
    this.parseTimestamp(timestamp).toISOString();

  /**
   * Returns the number of whole days elapsed since the given date.
   * Accepts either a numeric ms-since-epoch timestamp or an ISO date string.
   * Returns 0 for dates today or in the future.
   */
  getDaysSince = (date: string | number): number => {
    const parsed = isNaN(Number(date)) ? new Date(date) : new Date(Number(date));
    return Math.max(0, Math.floor((Date.now() - parsed.getTime()) / (1000 * 60 * 60 * 24)));
  };

  /**
   * Extract the epoch-ms number from a numeric-string timestamp, with a fallback.
   */
  getTimestampValue = (
    timestamp: string | number | null | undefined,
    fallback = 0
  ): number =>
    timestamp ? this.parseTimestamp(timestamp).getTime() : fallback;

  getStartOfToday = () => {
    const todayStart = new Date();
    todayStart.setUTCHours(0, 0, 0, 0);

    return todayStart;
  };

  getEndOfToday = () => {
    const todayEnd = new Date();
    todayEnd.setUTCHours(23, 59, 59, 999);

    return todayEnd;
  };

  getEndOfYesterday = () => {
    const currentDate = new Date();
    const yesterdayDate = new Date();
    yesterdayDate.setDate(currentDate.getDate() - 1);
    yesterdayDate.setUTCHours(23, 59, 59, 999);

    return yesterdayDate;
  };

  getMonthName = (monthIndex: number) => {
    return [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December'
    ][monthIndex];
  };
}

const ACCEPTED_DATE_FORMATS = [
  'YYYY-MM-DD',
  'YYYY-MM-DDTHH:mm:ssZ',
  'YYYY-MM-DDTHH:mm:ss.SSSZ'
];

export const isValidDateString = (value: string): boolean =>
  moment(value, ACCEPTED_DATE_FORMATS, true).isValid();

export default new DatesUtil();
